import {
	UntypedFormGroup,
	UntypedFormControl,
	Validators,
} from '@angular/forms';
import { BaseResponse } from './base-response.model';
import { DateTimeUtility } from '../utility/date-time.utility';
import { Validation } from './validation.model';

export interface ResidencyResponse extends BaseResponse {
	message: string;
	residency: ResidencyJSON;
}

export interface ResidencyJSON {
	idNumber: string;
	idDateOfExpiration: string;
	typeOfExitPermit: string;
	epDateOfExpiration: string;
	hmcCardNumber: string;
	hmcDateOfExpiration: string;
	qfWildcardNumber: string;
	qfWildcardExpiration: string;
	qdcPermitNumber: string;
	qdcPermitDateOfExpiration: string;
	driverLicense: string;
	dlDateOfExpiration: string;
}

export class Residency {
	constructor(
		public idNumber: string,
		public idDateOfExpiration: Date,
		public typeOfExitPermit: string,
		public epDateOfExpiration: Date,
		public hmcCardNumber: string,
		public hmcDateOfExpiration: Date,
		public qfWildcardNumber: string,
		public qfWildcardExpiration: Date,
		public qdcPermitNumber: string,
		public qdcPermitDateOfExpiration: Date,
		public driverLicense: string,
		public dlDateOfExpiration: Date
	) {}

	static fromJSON(json: ResidencyJSON): Residency {
		return Object.assign(Object.create(Residency.prototype), json, {
			idDateOfExpiration: json.idDateOfExpiration
				? DateTimeUtility.fromDatabaseDate(json.idDateOfExpiration)
				: null,
			epDateOfExpiration: json.epDateOfExpiration
				? DateTimeUtility.fromDatabaseDate(json.epDateOfExpiration)
				: null,
			hmcDateOfExpiration: json.hmcDateOfExpiration
				? DateTimeUtility.fromDatabaseDate(json.hmcDateOfExpiration)
				: null,
			qfWildcardExpiration: json.qfWildcardExpiration
				? DateTimeUtility.fromDatabaseDate(json.qfWildcardExpiration)
				: null,
			qdcPermitDateOfExpiration: json.qdcPermitDateOfExpiration
				? DateTimeUtility.fromDatabaseDate(
						json.qdcPermitDateOfExpiration
					)
				: null,
			dlDateOfExpiration: json.dlDateOfExpiration
				? DateTimeUtility.fromDatabaseDate(json.dlDateOfExpiration)
				: null,
		});
	}

	static createFormGroup(
		residency: Residency,
		validation: Validation,
		isHR: boolean
	): UntypedFormGroup {
		return new UntypedFormGroup({
			qdcPermitNumber: new UntypedFormControl(
				residency ? residency.qdcPermitNumber : '',
				[
					Validators.maxLength(validation.alnum.maxLength),
					Validators.pattern(validation.alnum.pattern),
				]
			),
			qdcPermitDateOfExpiration: new UntypedFormControl(
				residency ? residency.qdcPermitDateOfExpiration : null
			),
			...(isHR
				? {
						idNumber: new UntypedFormControl(
							residency ? residency.idNumber : '',
							[
								Validators.maxLength(
									validation.alnum.maxLength
								),
								Validators.pattern(validation.alnum.pattern),
							]
						),
						idDateOfExpiration: new UntypedFormControl(
							residency ? residency.idDateOfExpiration : null
						),
						typeOfExitPermit: new UntypedFormControl(
							residency ? residency.typeOfExitPermit : null
						),
						epDateOfExpiration: new UntypedFormControl(
							residency ? residency.epDateOfExpiration : null
						),
						hmcCardNumber: new UntypedFormControl(
							residency ? residency.hmcCardNumber : '',
							[
								Validators.maxLength(
									validation.alnum.maxLength
								),
								Validators.pattern(validation.alnum.pattern),
							]
						),
						hmcDateOfExpiration: new UntypedFormControl(
							residency ? residency.hmcDateOfExpiration : null
						),
						qfWildcardNumber: new UntypedFormControl(
							residency ? residency.qfWildcardNumber : '',
							[
								Validators.maxLength(
									validation.alnum.maxLength
								),
								Validators.pattern(validation.alnum.pattern),
							]
						),
						qfWildcardExpiration: new UntypedFormControl(
							residency ? residency.qfWildcardExpiration : null
						),
						driverLicense: new UntypedFormControl(
							residency ? residency.driverLicense : '',
							[
								Validators.maxLength(
									validation.alnum.maxLength
								),
								Validators.pattern(validation.alnum.pattern),
							]
						),
						dlDateOfExpiration: new UntypedFormControl(
							residency ? residency.dlDateOfExpiration : null
						),
					}
				: {}),
		});
	}

	static fromFormGroup(fg: UntypedFormGroup, isHR: boolean): Residency {
		const r: UntypedFormGroup = fg.get('residency') as UntypedFormGroup;
		return Object.assign(Object.create(Residency.prototype), {
			qdcPermitNumber: r.value['qdcPermitNumber'],
			qdcPermitDateOfExpiration: DateTimeUtility.toDatabaseDateString(
				r.value['qdcPermitDateOfExpiration']
			),
			...(isHR
				? {
						idNumber: r.value['idNumber'],
						idDateOfExpiration:
							DateTimeUtility.toDatabaseDateString(
								r.value['idDateOfExpiration']
							),
						typeOfExitPermit: r.value['typeOfExitPermit'],
						epDateOfExpiration:
							DateTimeUtility.toDatabaseDateString(
								r.value['epDateOfExpiration']
							),
						hmcCardNumber: r.value['hmcCardNumber'],
						hmcDateOfExpiration:
							DateTimeUtility.toDatabaseDateString(
								r.value['hmcDateOfExpiration']
							),
						qfWildcardNumber: r.value['qfWildcardNumber'],
						qfWildcardExpiration:
							DateTimeUtility.toDatabaseDateString(
								r.value['qfWildcardExpiration']
							),
						driverLicense: r.value['driverLicense'],
						dlDateOfExpiration:
							DateTimeUtility.toDatabaseDateString(
								r.value['dlDateOfExpiration']
							),
					}
				: {}),
		});
	}
}
